import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, MapPin, CheckCircle2, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Card, SectionHeader, PrimaryBtn } from "./ui";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-BE", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) : "-");
const hoursUntil = (d) => { if (!d) return null; return (new Date(d).getTime() - Date.now()) / 36e5; };

export default function ReplacementsAdmin() {
  const [open, setOpen] = useState([]);
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null);
  const [cands, setCands] = useState([]);
  const [checked, setChecked] = useState({});
  const [pc, setPc] = useState("");

  const load = useCallback(() => {
    api.get("/admin/replacements", { params: { status: "open" } }).then((r) => setOpen(r.data)).catch(() => {});
    api.get("/admin/replacements", { params: { status: "filled" } }).then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDialog = async (rep) => {
    setActive(rep); setChecked({}); setPc(rep.school_postal_code || "");
    try { const r = await api.get(`/admin/replacements/${rep.id}/candidates`); setCands(r.data.candidates); }
    catch { setCands([]); }
  };
  const savePc = async () => {
    try { await api.patch(`/admin/replacements/${active.id}`, { school_postal_code: pc }); const r = await api.get(`/admin/replacements/${active.id}/candidates`); setCands(r.data.candidates); toast.success("Code postal enregistré"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const invite = async () => {
    const ids = Object.keys(checked).filter((k) => checked[k]);
    if (ids.length === 0) { toast.error("Cochez au moins un animateur"); return; }
    try { const r = await api.post(`/admin/replacements/${active.id}/invite`, { animator_ids: ids }); toast.success(`${r.data.invited} animateur(s) contacté(s) par email`); setActive(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader title={`Remplacements urgents (${open.length})`} />
        {open.length === 0 ? <p className="text-slate-500" data-testid="no-open-replacements">Aucun remplacement en attente. 🎉</p> : (
          <div className="space-y-3" data-testid="open-replacements-list">
            {open.map((rep) => {
              const h = hoursUntil(rep.slot_date);
              const urgent = h != null && h <= 48;
              const invited = (rep.candidates || []).length;
              return (
                <div key={rep.id} className={`flex flex-wrap items-center gap-3 rounded-2xl p-4 border ${urgent ? "border-rose-300 bg-rose-50" : "border-amber-200 bg-amber-50"}`} data-testid={`replacement-${rep.id}`}>
                  <span className={`grid place-items-center w-10 h-10 rounded-xl ${urgent ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-600"}`}><AlertTriangle className="w-5 h-5" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800">{rep.reservation_title} · <span className="capitalize">{fmt(rep.slot_date)}</span></p>
                    <p className="text-xs text-slate-500">École : {rep.school || "-"} · Désistement de {rep.original_animator_name || "-"} · {invited} invité(s)</p>
                    {urgent && <span className="inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-200 text-rose-700">⚠️ Moins de 48h</span>}
                  </div>
                  <PrimaryBtn onClick={() => openDialog(rep)} data-testid={`replacement-manage-${rep.id}`}>Trouver un remplaçant</PrimaryBtn>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader title="Historique des remplacements" />
        {history.length === 0 ? <p className="text-slate-500">Aucun remplacement effectué pour le moment.</p> : (
          <div className="space-y-2" data-testid="replacements-history">
            {history.map((rep) => (
              <div key={rep.id} className="flex items-center gap-3 border border-slate-100 rounded-2xl p-3">
                <span className="grid place-items-center w-9 h-9 rounded-xl bg-emerald-100 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm">{rep.reservation_title} · <span className="capitalize">{fmt(rep.slot_date)}</span></p>
                  <p className="text-xs text-slate-500">{rep.original_animator_name} → <strong className="text-emerald-700">{rep.filled_by_animator_name}</strong></p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="replacement-dialog">
          <DialogHeader><DialogTitle>Remplaçant pour {active?.reservation_title}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="flex items-end gap-2 p-3 rounded-2xl bg-slate-50">
                <div className="flex-1">
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-400">Code postal de l'école (pour la priorité de zone)</label>
                  <Input value={pc} onChange={(e) => setPc(e.target.value)} placeholder="ex: 6000" className="rounded-xl mt-1" data-testid="replacement-postal-input" />
                </div>
                <button onClick={savePc} data-testid="replacement-postal-save" className="text-xs font-bold px-3 py-2.5 rounded-full bg-slate-900 text-white hover:bg-slate-700">Appliquer</button>
              </div>
              <p className="text-sm text-slate-500">Cochez les animateurs à contacter. Ils recevront un email avec un bouton de confirmation. <strong>Le premier à confirmer obtient l'atelier.</strong></p>
              <div className="space-y-2" data-testid="replacement-candidates">
                {cands.map((c) => (
                  <label key={c.id} className={`flex items-center gap-3 border rounded-2xl p-3 cursor-pointer transition-colors ${checked[c.id] ? "border-sky-400 bg-sky-50" : "border-slate-100 hover:border-slate-200"}`} data-testid={`candidate-${c.id}`}>
                    <input type="checkbox" checked={!!checked[c.id]} disabled={c.already_invited} onChange={(e) => setChecked({ ...checked, [c.id]: e.target.checked })} data-testid={`candidate-check-${c.id}`} className="w-4 h-4 accent-sky-500" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-sm">{c.name}</p>
                      <p className="text-xs text-slate-400">{(c.postal_codes || []).join(", ") || "aucun code postal"}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {c.in_zone && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><MapPin className="w-3 h-3" /> Zone proche{c.distance != null ? ` (${c.distance})` : ""}</span>}
                      {!c.in_zone && c.distance != null && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Écart {c.distance}</span>}
                      {c.has_availability && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 flex items-center gap-1"><Clock className="w-3 h-3" /> Dispo déclarée</span>}
                      {c.candidate_status === "declined" && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-600" data-testid={`candidate-declined-${c.id}`}>A refusé</span>}
                      {c.candidate_status === "confirmed" && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">A confirmé</span>}
                      {c.candidate_status === "invited" && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Invité</span>}
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setActive(null)} className="text-sm font-bold px-4 py-2 rounded-full bg-slate-100 text-slate-700">Fermer</button>
                <PrimaryBtn onClick={invite} data-testid="replacement-invite-btn">Contacter les animateurs cochés</PrimaryBtn>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
